import { FormCheck, FormGroup } from "react-bootstrap";
import "./create-infographic.component.scss";
import { useEffect, useState } from "react";
import Select from "react-select";
import LoadingModal from "../../../modal/loading/loading";
import Button from '@mui/material/Button';
import { selectCreateColourStyles } from "../../../config/form-select.config";
import { useNavigate } from "react-router-dom";
import { sizeRadioOptions } from "../../../config/infographic-size.config";
import { templateRadioOptions, toneOptions } from "../../../config/infographic-option.config";
import { Media } from "../../../model/media.model";
import MediaService from "../../../services/media.service";
import CircularProgress from '@mui/material/CircularProgress';
import { getUniqueSpeakerList } from "../../../utility/function.utility";
import InfographicService from "../../../services/infographic.service";
import { useSnackbar } from "../../../utility/snackbar.utility";
import CustomSnackbar from "../../snackbar/snackbar.component";
import { infographicTemplatePrompt } from "../../../config/infographic-prompt.config";

interface NameOption {
    value: string;
    label: string;
}

const CreateInfoGraphicComponent = ({ id }) => {

    const navigate = useNavigate();
    const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();

    const [selectedOptions, setSelectedOptions] = useState<NameOption | null>(null);
    const [title, setTitle] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [selectedSize, setSelectedSize] = useState("");
    const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
    const [isShowLoading, setShowLoading] = useState<boolean>(false);
    const [isDisabled, setIsDisabled] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [media, setMedia] = useState<Media>();
    const [speakerList, setSpeakerList] = useState<string[]>([]);
    const [errorInfoId, setErrorInfoId] = useState('');

    useEffect(() => {
        fetchMedia();
    }, []);

    useEffect(() => {
        if (title && selectedTemplate && selectedSize && selectedOptions) {
            setIsDisabled(false);
        } else {
            setIsDisabled(true);
        }
    }, [title, selectedTemplate, selectedSize, selectedOptions]);

    const fetchMedia = async () => {
        try {
            setIsLoading(true);
            const response = await MediaService.getMediaById(id);
            setMedia(response.data);
            setSpeakerList(getUniqueSpeakerList(response.data.transcripts, false));
        } catch (error) {
            setIsLoading(false);
            showSnackbar('', "error");
        } finally {
            setIsLoading(false);
        }
    }

    const handleToneChange = (event) => {
        setSelectedOptions(event);
    };

    const handleTemplateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedTemplate(event.target.value);
    };

    const handleSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSelectedSize(event.target.value);
    };

    const handleSpeakerChange = (event) => {
        const { id, checked } = event.target;

        setSelectedSpeakers((prev) =>
            checked ? [...prev, id] : prev.filter((speaker) => speaker !== id)
        );
    };

    const handleCancel = () => {
        navigate(`/detail/${id}`);
    }

    const onSubmit = async () => {
        if (!title || !selectedTemplate || !selectedSize || !selectedOptions) return;

        setShowLoading(true);

        try {
            await handleExistingErrorInfo();
            await handleAddInfographic();
        } catch (error) {
            setShowLoading(false);
            showSnackbar('', "error");
        }
    };

    const handleExistingErrorInfo = async () => {
        if (errorInfoId) {
            await InfographicService.getInfographic(id, errorInfoId);
            setShowLoading(false);
            navigate(`/infographic/edit/${id}/${errorInfoId}`);
        }
    };

    const handleAddInfographic = async () => {
        const templateId = Number(selectedTemplate?.split(' ')[1]) || 1;
        const focusSpeakers = selectedSpeakers?.map(item => Number(item.split(' ')[1])) || [];
        const tone = selectedOptions?.label || '';
        const block = infographicTemplatePrompt[templateId][selectedSize].block;
        const maxQuote = infographicTemplatePrompt[templateId][selectedSize].block_max_quote_char;
        const maxTitle = infographicTemplatePrompt[templateId][selectedSize].block_max_title_char;

        const addInfoResponse = await InfographicService.addInfo(id, templateId, selectedSize, title, focusSpeakers, tone, block, maxQuote, maxTitle);
        const infoId = addInfoResponse.data.id;

        setErrorInfoId(infoId);
        await InfographicService.getInfographic(id, infoId);

        setShowLoading(false);
        navigate(`/infographic/edit/${id}/${infoId}`);
    };

    return (
        <>
            {
                isLoading ?
                    <CircularProgress className="mt-5" /> :
                    <div className="create-info-control">

                        <div className="create-info-border-control">
                            <p className="create-info-title">สร้าง Infographic</p>
                            <span className="create-info-label">
                                1. ใส่ข้อมูลของ Infographic<span className="create-info-required">*</span>
                            </span>
                            <div className="create-info-form-control">

                                <input
                                    className="input-default create-info-form-input"
                                    type="text"
                                    id="search"
                                    placeholder="ชื่อ infographic"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                />


                                <Select
                                    className="create-info-form-dropdown"
                                    options={toneOptions}
                                    value={selectedOptions}
                                    onChange={handleToneChange}
                                    placeholder="เลือก Tone"
                                    classNamePrefix="select"
                                    styles={selectCreateColourStyles}
                                />

                            </div>
                        </div>

                        <div className="create-info-border-control">
                            <span className="create-info-label">
                                2. เลือก Style<span className="create-info-required">*</span>
                            </span>

                            <FormGroup className="create-info-form-control">
                                {
                                    templateRadioOptions.map((template, index) => (
                                        <label key={template.value} className="create-info-form-item image">
                                            <FormCheck
                                                id={template.value}
                                                type="radio"
                                                className="custom-checkbox"
                                                name="templateGroup"
                                                value={template.value}
                                                label={`Style ${index + 1}`}
                                                onChange={handleTemplateChange}
                                            />
                                            <img src={template.src} alt={`template ${template.value}`} className="create-info-form-template-img"></img>
                                        </label>
                                    ))
                                }
                            </FormGroup>
                        </div>

                        <div className="create-info-border-control">
                            <span className="create-info-label">
                                3. เลือกขนาด<span className="create-info-required">*</span>
                            </span>

                            <FormGroup className="create-info-form-control">
                                {
                                    sizeRadioOptions.map((size) => (
                                        <label key={size.value} className="create-info-form-item">
                                            <FormCheck
                                                id={size.value}
                                                type="radio"
                                                className="custom-checkbox"
                                                name="sizeGroup"
                                                value={size.value}
                                                label={size.value}
                                                onChange={handleSizeChange}
                                            />
                                            <img src={size.src} alt={size.value} />
                                        </label>
                                    ))
                                }
                            </FormGroup>
                        </div>

                        <div className="create-info-border-control">
                            <p className="create-info-label">4. เลือกผู้พูดที่ต้องการไฮไลต์เนื้อหา (ไม่บังคับ)</p>
                            <div className="create-info-form-control speaker-wrapper">
                                {
                                    speakerList && speakerList.map((item) => (
                                        <div key={item} className="create-info-form-item">
                                            <FormCheck
                                                className="custom-checkbox"
                                                type="checkbox"
                                                id={item}
                                                label={`เนื้อหาของ${item.replace('Speaker', 'ผู้พูด')}`}
                                                checked={selectedSpeakers.includes(item)}
                                                onChange={handleSpeakerChange}
                                            />
                                        </div>
                                    ))
                                }
                            </div>
                        </div>

                        <div className="create-info-button-control">
                            <Button
                                variant="outlined"
                                className="create-info-cancel-button"
                                onClick={handleCancel}
                            >
                                ยกเลิก
                            </Button>

                            <Button
                                variant="contained"
                                className="create-info-submit-button"
                                onClick={onSubmit}
                                disabled={isDisabled}
                            >
                                ดำเนินการต่อ
                            </Button>
                        </div>
                    </div>
            }

            {
                isShowLoading &&
                <LoadingModal show={isShowLoading} onClose={() => setShowLoading(false)} message={"ระบบกำลังทำการ generate เนื้อหา และ infographic<br/> อาจใช้เวลาสักครู่กรุณาอย่าปิดหน้าต่างนี้"}></LoadingModal>
            }

            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                duration={snackbar.duration}
                onClose={hideSnackbar}
            />
        </>
    )

}

export default CreateInfoGraphicComponent;